import { useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { useModal } from '../contexts/ModalContext';

/**
 * The plan change awaiting approval, and the two buttons that resolve it.
 *
 * This is the visible half of the draft gate: a price change does not take
 * effect when an admin saves it. It is held here, with the Terms revision it
 * would publish, until somebody approves both together. Until then customers
 * pay the old price and read the old Terms — a consistent state, and the
 * reason the banner has to say so plainly rather than looking like a toast.
 *
 * Rendered at the top of Plans and of Legal Documents, because the admin who
 * made the change and the admin who has to approve it are not always the same
 * person and do not start from the same screen.
 */

interface PendingChange {
  /**
   * Two kinds park here: a price/copy edit on a tier, and a feature moved
   * between tiers. Only one of `tier` / `featureId` is set, so neither is
   * rendered directly — `summary` is written server-side to read correctly
   * for both ("plus: USD yearly: 5900 → 8900", "Caregiver alerts: added to
   * plus") and is the only thing this banner shows.
   */
  kind?: 'plan' | 'feature';
  tier?: string;
  featureId?: string;
  summary: string;
  requestedBy: { uid: string; displayName: string };
  requestedAt: string;
  draftIds: Record<string, string>;
  trigger?: { kind: string; tier: string };
}

interface PublishImpact {
  revision: number;
  materialChange: boolean;
}

interface PendingResponse {
  pending: PendingChange | null;
  impact?: Record<string, PublishImpact | null>;
}

const DOCUMENT_LABEL: Record<string, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
};

export function PendingPlanChange({ onResolved }: { onResolved?: () => void }) {
  const modal = useModal();
  const { data, loading, error, reload } = useApiResource<PendingResponse>(
    () => apiGet<PendingResponse>('/admin/legal/pending-change'),
    [],
  );

  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const pending = data?.pending ?? null;
  if (loading && !data) return null;
  if (error) return <div className="error-banner">{error}</div>;
  if (!pending) return null;

  const documents = Object.keys(pending.draftIds);
  // A material publish asks every user to re-accept on next open. That is the
  // single most consequential thing on this screen, so it is stated before the
  // click, not discovered after it.
  const material = documents.some((d) => data?.impact?.[d]?.materialChange);

  const run = async (kind: 'approve' | 'reject', fn: () => Promise<void>) => {
    setBusy(kind);
    setActionError(null);
    try {
      await fn();
      reload();
      onResolved?.();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    const confirmed = await modal.confirm({
      title: 'Approve this plan change?',
      message:
        `This applies ${pending.summary} and publishes ${documents
          .map((d) => `${DOCUMENT_LABEL[d] ?? d} v${data?.impact?.[d]?.revision ?? '?'}`)
          .join(' and ')}.` +
        (material
          ? '\n\nThe new text is a material change, so every user will be asked to re-accept the Terms the next time they open the app.'
          : '\n\nThe new text is not a material change, so nobody will be re-prompted.'),
      confirmLabel: 'Apply and publish',
    });
    if (!confirmed) return;
    await run('approve', async () => {
      await apiPost('/admin/legal/pending-change/approve');
    });
  };

  const reject = async () => {
    const reason = await modal.promptText({
      title: 'Reject this plan change?',
      message: `Nothing will be applied and the draft ${documents.length > 1 ? 'revisions' : 'revision'} will be discarded. Why are you rejecting it?`,
      confirmLabel: 'Reject and discard',
    });
    if (reason === null) return;
    await run('reject', async () => {
      await apiPost('/admin/legal/pending-change/reject', { reason });
    });
  };

  return (
    <div className="card pending-change-card">
      <div className="card-header">
        <h2 className="card-title">Awaiting approval — this change is not live</h2>
      </div>
      <div className="card-body">
        <p>
          <strong>{pending.summary}</strong>
        </p>
        <p className="cell-sub">
          Requested by {pending.requestedBy.displayName} on{' '}
          {new Date(pending.requestedAt).toLocaleString()}. Customers are still paying the old
          price and reading the old documents until this is approved.
        </p>

        <div className="table-wrap">
          <table className="data-table dt-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Would publish</th>
                <th>Re-prompts users</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d}>
                  <td>{DOCUMENT_LABEL[d] ?? d}</td>
                  <td>v{data?.impact?.[d]?.revision ?? '—'}</td>
                  <td>{data?.impact?.[d]?.materialChange ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {material && (
          <div role="alert" className="error-banner" data-testid="pending-material-warning">
            Approving this asks every user to re-accept the Terms of Service the next time they
            open the app.
          </div>
        )}

        {actionError && (
          <div role="alert" className="error-banner">
            {actionError}
          </div>
        )}

        <div className="plan-card-footer">
          <button type="button" className="btn" onClick={reject} disabled={busy !== null}>
            {busy === 'reject' ? 'Rejecting…' : 'Reject'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={approve}
            disabled={busy !== null}
          >
            {busy === 'approve' ? 'Applying…' : 'Approve and publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
